import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Loader2 } from "lucide-react";

/**
 * Handles the post-Google-OAuth callback. Extracts session_id from
 * URL fragment, exchanges it with the backend, then redirects to /internal/dashboard.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [error, setError] = useState(null);
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      setError("No session_id in URL fragment");
      return;
    }
    const sessionId = decodeURIComponent(m[1]);

    (async () => {
      try {
        await api.post("/auth/google", null, {
          headers: { "X-Session-ID": sessionId },
        });
        // Clean URL
        window.history.replaceState({}, "", window.location.pathname);
        await refresh();
        nav("/internal/dashboard", { replace: true });
      } catch (e) {
        setError(e?.response?.data?.detail || "Authentication failed");
      }
    })();
  }, [nav, refresh]);

  return (
    <div className="grain min-h-screen flex items-center justify-center">
      <div className="max-w-md mx-auto p-8">
        {error ? (
          <>
            <div className="tick-label text-rose-400 mb-2">/ Auth failed</div>
            <h1 className="font-display font-black text-xl text-slate-100 mb-3">Sign-in could not be completed.</h1>
            <p className="text-sm text-slate-400 mb-4">{error}</p>
            <button onClick={() => nav("/login")} className="border border-slate-700 hover:border-sky-500 hover:text-sky-400 text-slate-200 font-mono text-[11px] tracking-[0.2em] uppercase px-4 py-2.5">
              Back to Login
            </button>
          </>
        ) : (
          <div className="text-center">
            <Loader2 className="w-7 h-7 text-sky-400 animate-spin mx-auto mb-3" />
            <div className="tick-label text-sky-400">/ Completing sign-in...</div>
          </div>
        )}
      </div>
    </div>
  );
}
